import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';

const ROLES = ['admin', /*'staff',*/ 'trainer'/*, 'student'*/];
const MODULES = ['home', 'students', 'records', 'reports', 'intakes', 'sessions', 'users', 'permissions', 'audit'];
const ACTIONS = ['view', 'create', 'update', 'delete'];

export default function PermissionsPage() {
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRole, setActiveRole] = useState('admin');

  useEffect(() => {
    api.get('/api/permissions')
      .then(res => {
        const map = {};
        (res.data.data || []).forEach(p => {
          if (!map[p.role]) map[p.role] = {};
          map[p.role][p.module] = {
            view: !!p.can_view, create: !!p.can_create,
            update: !!p.can_update, delete: !!p.can_delete,
          };
        });
        setPerms(map);
      })
      .catch(() => toast.error('Failed to load permissions.'))
      .finally(() => setLoading(false));
  }, []);

  function toggle(role, module, action) {
    setPerms(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...(prev[role]?.[module] || {}),
          [action]: !(prev[role]?.[module]?.[action]),
        },
      },
    }));
  }

  function setAllForRole(role, value) {
    const updated = {};
    MODULES.forEach(m => {
      updated[m] = {};
      ACTIONS.forEach(a => { updated[m][a] = value; });
    });
    setPerms(prev => ({ ...prev, [role]: updated }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const permissions = [];
      Object.entries(perms).forEach(([role, modules]) => {
        Object.entries(modules).forEach(([module, actions]) => {
          permissions.push({
            role, module,
            can_view: actions.view ? 1 : 0,
            can_create: actions.create ? 1 : 0,
            can_update: actions.update ? 1 : 0,
            can_delete: actions.delete ? 1 : 0,
          });
        });
      });
      await api.put('/api/permissions', { permissions });
      toast.success('Permissions saved.');
    } catch { toast.error('Failed to save permissions.'); }
    finally { setSaving(false); }
  }

  const actionIcon = { view: 'bi-eye', create: 'bi-plus-circle', update: 'bi-pencil', delete: 'bi-trash' };
  const actionColor = {
    view:   'var(--tmis-teal)',
    create: 'var(--tmis-present)',
    update: 'var(--tmis-late)',
    delete: 'var(--tmis-absent)',
  };
  // Header cells sit on the navy .tmis-table thead background (constant across
  // themes), so they need theme-independent "on navy" colors rather than
  // actionColor, which is tuned for the light table body surface.
  const actionColorHeader = {
    view:   'var(--tmis-teal-on-navy)',
    create: 'var(--tmis-present-on-navy)',
    update: 'var(--tmis-late-on-navy)',
    delete: 'var(--tmis-absent-on-navy)',
  };

  return (
    <div>
      <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1>Permissions</h1>
          <p>Control what each role can access and do</p>
        </div>
        <button className="btn btn-tmis-primary btn-sm d-flex align-items-center gap-2" onClick={handleSave} disabled={saving}>
          {saving && <span className="spinner-border spinner-border-sm" />}
          <i className="bi bi-save" />Save Permissions
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: '#17a2b8' }} /></div>
      ) : (
        <>
          {/* Role tabs */}
          <div className="tmis-card mb-3 d-flex gap-2 flex-wrap align-items-center">
            <div className="filter-tab-group">
              {ROLES.map(r => (
                <button key={r} className={`filter-tab ${activeRole === r ? 'active' : ''}`} onClick={() => setActiveRole(r)}>
                  {r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </button>
              ))}
            </div>
            <div className="ms-auto d-flex gap-2">
              <button className="btn btn-sm btn-outline-success" onClick={() => setAllForRole(activeRole, true)}>
                <i className="bi bi-check-all me-1" />Grant All
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => setAllForRole(activeRole, false)}>
                <i className="bi bi-x-circle me-1" />Revoke All
              </button>
            </div>
          </div>

          <div className="tmis-card">
            <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
              <i className="bi bi-shield-check me-1 text-warning" />
              Super Admin always has full access and cannot be restricted.
            </p>
            <div className="table-responsive">
              <table className="table tmis-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 200 }}>Module</th>
                    {ACTIONS.map(a => (
                      <th key={a} className="text-center" style={{ width: 110 }}>
                        <span style={{ color: actionColorHeader[a] }}>
                          <i className={`bi ${actionIcon[a]} me-1`} />
                          {a.charAt(0).toUpperCase() + a.slice(1)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(module => (
                    <tr key={module}>
                      <td>
                        <span className="fw-semibold" style={{ textTransform: 'capitalize' }}>
                          <i className="bi bi-grid me-2 text-muted" />
                          {module}
                        </span>
                      </td>
                      {ACTIONS.map(action => {
                        const checked = perms[activeRole]?.[module]?.[action] || false;
                        return (
                          <td key={action} className="text-center">
                            <div className="form-check d-flex justify-content-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: actionColor[action] }}
                                checked={checked}
                                onChange={() => toggle(activeRole, module, action)}
                                id={`perm-${activeRole}-${module}-${action}`}
                                aria-label={`${activeRole} ${module} ${action}`}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
